import { Check, Copy, Monitor } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { DESKTOP_HOST, isMobileRoute, useIsMobile } from '@/lib/mobile'

type CopyState = 'pronto' | 'copiado' | 'falhou'

/**
 * A página, ou o aviso de computador: no celular, só as telas de
 * `isMobileRoute` abrem; no computador, todas.
 */
export function DeviceGate({
  pathname,
  search,
  children,
}: {
  pathname: string
  search: string
  children: React.ReactNode
}) {
  const isMobile = useIsMobile()
  if (!isMobile || isMobileRoute(pathname)) return <>{children}</>
  return <DesktopOnly href={`${window.location.origin}${pathname}${search}`} />
}

/**
 * O que o celular mostra nas telas feitas para o computador (Onda 6.1).
 * "Copiar link" leva o endereço DESTA tela, para colar no computador e cair no
 * mesmo lugar. Sem área de transferência (navegador antigo, permissão negada),
 * o endereço fica à vista para copiar à mão.
 */
export function DesktopOnly({ href }: { href: string }) {
  const [copy, setCopy] = React.useState<CopyState>('pronto')

  React.useEffect(() => {
    if (copy !== 'copiado') return
    const timer = window.setTimeout(() => setCopy('pronto'), 2500)
    return () => window.clearTimeout(timer)
  }, [copy])

  async function copyLink() {
    try {
      if (!navigator.clipboard) throw new Error('sem área de transferência')
      await navigator.clipboard.writeText(href)
      setCopy('copiado')
    } catch {
      setCopy('falhou')
    }
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-2 py-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-md bg-muted text-muted-foreground" aria-hidden>
        <Monitor className="h-6 w-6" />
      </div>
      <div className="max-w-xs space-y-1.5">
        <h1 className="text-[15px] font-semibold tracking-tight">Esta tela foi feita para o computador.</h1>
        <p className="text-[13px] text-muted-foreground">
          Abra <span className="font-medium text-foreground">{DESKTOP_HOST}</span> no navegador.
        </p>
      </div>
      <Button onClick={() => void copyLink()} className="min-w-40">
        {copy === 'copiado' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        {copy === 'copiado' ? 'Link copiado' : 'Copiar link'}
      </Button>
      <p role="status" aria-live="polite" className="max-w-xs break-all text-[12px] text-muted-foreground">
        {copy === 'falhou' ? (
          <>
            Não deu para copiar daqui. O endereço é <span className="select-all text-foreground">{href}</span>
          </>
        ) : copy === 'copiado' ? (
          'Cole no navegador do computador para abrir esta tela.'
        ) : null}
      </p>
    </div>
  )
}
